/* The five growing systems: the single place their names and plant-ID patterns live.
 *
 * `id` is what devices and the database use (the ESP32 firmware posts to
 * /units/DWC1/..., rows store unit_id 'DWC1'), so it must never change. `name` is
 * what people read; every page shows `name`, never the raw id.
 *
 * `plantPattern` tells which system a plant belongs to from its ID. The patterns do
 * not overlap: rack IDs are a letter plus three digits (A231), aero IDs a letter
 * plus one digit (F6), trough IDs T plus three digits (T111). An archived plant
 * carries a `_D<timestamp>` suffix, which systemForPlant() strips first. */

export const SYSTEMS = [
  { id: 'DWC1', name: 'Rack A', type: 'Deep Water Culture', plantPattern: /^A[1-4][1-4][1-7]$/ },
  { id: 'DWC2', name: 'Rack B', type: 'Deep Water Culture', plantPattern: /^B[1-4][1-4][1-7]$/ },
  { id: 'DWC3', name: 'Rack C', type: 'Deep Water Culture', plantPattern: /^C[1-4][1-4][1-7]$/ },
  { id: 'AERO', name: 'Aero Tower', type: 'Aeroponic', plantPattern: /^[A-J][1-8]$/ },
  { id: 'TROUGH', name: 'Trough', type: 'Trough', plantPattern: /^T\d{3}$/ },
];

export const SYSTEM_IDS = SYSTEMS.map(s => s.id);

const BY_ID = Object.fromEntries(SYSTEMS.map(s => [s.id, s]));

/** Display name for a system id; anything unknown is shown as given. */
export const systemName = id => BY_ID[id]?.name ?? id;
/** Names for a list of IDs, or a comma-separated string ('DWC1,DWC2' → 'Rack A, Rack B'). */
export const systemNames = ids => (Array.isArray(ids) ? ids : String(ids || '').split(','))
  .filter(Boolean).map(systemName).join(', ');

/** The system a plant sits in, from its ID, or null. */
export const systemForPlant = plantId => {
  const base = String(plantId || '').replace(/_D\d+$/, '');
  return SYSTEMS.find(s => s.plantPattern.test(base)) || null;
};

/** Where a plant sits, from its ID. Racks: A + shelf (1-4, 1 at the top) + pipe (1-4, 1 at
 *  the front) + cup (1-7, 1 at the left end), as the labels on the rack read (checked against
 *  Rack A's photos, 26-27 Oct 2026). Aero tower: tier letter A-J (A at the top) + pocket 1-8
 *  round the ring (labels A3, A4 on the top tier, B3 below). Trough rack: T + shelf (1-4,
 *  1 at the BOTTOM, unlike the racks) + trough (1 at the back, 2 at the front) + position
 *  (1-9), as the farm confirmed 27 Oct 2026. Returns null for anything else (archived IDs too). */
export const plantPosition = plantId => {
  const id = String(plantId || '');
  let m = id.match(/^([ABC])([1-4])([1-4])([1-7])$/);
  if (m) return { kind: 'rack', rack: m[1], shelf: +m[2], pipe: +m[3], cup: +m[4] };
  m = id.match(/^([A-J])([1-8])$/);
  if (m) return { kind: 'aero', tier: m[1], pocket: +m[2] };
  m = id.match(/^T([1-4])([12])([1-9])$/);
  if (m) return { kind: 'trough', shelf: +m[1], trough: +m[2], position: +m[3] };
  return null;
};
